import { useState, useEffect, useCallback } from "react";
import { getCart, updateItemQty, removeItemSafe, applyCoupon, removeCoupon, clearCart } from "./api.js";
import CartItems from "./components/CartItems.jsx";
import CartSidebar from "./components/CartSidebar.jsx";
import CartEmpty from "./components/CartEmpty.jsx";

export default function CartApp({ checkoutUrl, shopUrl }) {
	const [cart, setCart] = useState(null);
	const [loading, setLoading] = useState(true);
	const [busyKeys, setBusyKeys] = useState(new Set());
	const [error, setError] = useState(null);

	// Load cart
	useEffect(() => {
		getCart()
			.then(setCart)
			.catch(() => setError("Failed to load cart"))
			.finally(() => setLoading(false));
	}, []);

	// Note: the Zippy CRM points-tender listener moved to CheckoutApp in
	// plugin v1.13.0. Cart no longer hosts the redemption widget, so there's
	// nothing for this surface to subscribe to.

	const markBusy = useCallback((key, busy) => {
		setBusyKeys((prev) => {
			const next = new Set(prev);
			busy ? next.add(key) : next.delete(key);
			return next;
		});
	}, []);

	const handleUpdateQty = useCallback(async (key, qty) => {
		markBusy(key, true);
		try {
			const updated = await updateItemQty(key, qty);
			setCart(updated);
		} catch {
			setError("Failed to update quantity");
		} finally {
			markBusy(key, false);
		}
	}, [markBusy]);

	const handleRemove = useCallback(async (key) => {
		markBusy(key, true);
		try {
			const updated = await removeItemSafe(key);
			setCart(updated);
		} catch {
			setError("Failed to remove item");
		} finally {
			markBusy(key, false);
		}
	}, [markBusy]);

	// Remove every line of a racket build in one go. Removing the racket line
	// alone already cascades its string/overgrip lines away server side (see
	// BuildGroup::reconcile()), so this both tolerates that ("safe" remove)
	// and skips a key the previous iteration's response shows is already gone.
	const handleRemoveBuild = useCallback(async (keys) => {
		keys.forEach((k) => markBusy(k, true));
		try {
			let updated;
			for (const k of keys) {
				if (updated && !updated.items.some((line) => line.key === k)) continue;
				updated = await removeItemSafe(k);
			}
			if (updated) setCart(updated);
		} catch {
			setError("Failed to remove build");
			getCart().then(setCart).catch(() => {});
		} finally {
			keys.forEach((k) => markBusy(k, false));
		}
	}, [markBusy]);

	const handleClearCart = useCallback(async () => {
		if (!cart?.items?.length) return;
		setLoading(true);
		try {
			const updated = await clearCart(cart.items);
			setCart(updated);
		} catch {
			setError("Failed to clear cart");
		} finally {
			setLoading(false);
		}
	}, [cart]);

	const handleApplyCoupon = useCallback(async (code) => {
		try {
			const updated = await applyCoupon(code);
			setCart(updated);
			return null;
		} catch (err) {
			return err.message;
		}
	}, []);

	const handleRemoveCoupon = useCallback(async (code) => {
		try {
			const updated = await removeCoupon(code);
			setCart(updated);
		} catch {
			setError("Failed to remove coupon");
		}
	}, []);

	// Dismiss error
	useEffect(() => {
		if (!error) return;
		const t = setTimeout(() => setError(null), 4000);
		return () => clearTimeout(t);
	}, [error]);

	// Loading skeleton
	if (loading && !cart) {
		return (
			<div className="zc">
				<div className="zc__skeleton">
					<div className="zc__skeleton-items">
						{[1, 2, 3].map((i) => (
							<div key={i} className="zc__skeleton-row" />
						))}
					</div>
					<div className="zc__skeleton-sidebar" />
				</div>
			</div>
		);
	}

	// Empty cart
	if (!cart?.items?.length) {
		return (
			<div className="zc">
				<CartEmpty shopUrl={shopUrl} />
			</div>
		);
	}

	return (
		<div className="zc">
			{error && <div className="zc__error">{error}</div>}

			<div className="zc__layout">
				<CartItems
					items={cart.items}
					busyKeys={busyKeys}
					loading={loading}
					onUpdateQty={handleUpdateQty}
					onRemove={handleRemove}
					onRemoveBuild={handleRemoveBuild}
					onClearCart={handleClearCart}
				/>
				<CartSidebar
					cart={cart}
					checkoutUrl={checkoutUrl}
					onApplyCoupon={handleApplyCoupon}
					onRemoveCoupon={handleRemoveCoupon}
				/>
			</div>
		</div>
	);
}
